import React, { useEffect, useRef, useState } from 'react';
import { C } from './UI';

export interface AddressResult {
    address: string;
    lat: number;
    lng: number;
    city?: string;
    state?: string;
    county?: string;
    postalCode?: string;
    country?: string;
}

// Maps Google's address_components (type -> {short_name, long_name}) into the structured
// fields Stripe Tax's Calculation API needs (city/state/postal code/country), plus county
// (administrative_area_level_2 in US addresses) for service-area coverage checks.
export function parseAddressComponents(components: any[] | undefined): { city?: string; state?: string; county?: string; postalCode?: string; country?: string } {
    if (!components) return {};
    const find = (type: string) => components.find(c => c.types.includes(type));
    return {
        city: find('locality')?.long_name ?? find('sublocality')?.long_name,
        state: find('administrative_area_level_1')?.short_name,
        county: find('administrative_area_level_2')?.long_name,
        postalCode: find('postal_code')?.long_name,
        country: find('country')?.short_name,
    };
}

interface Props {
    label: string;
    value: string;
    onChange: (result: AddressResult) => void;
    placeholder?: string;
    required?: boolean;
}

declare global {
    interface Window {
        google: any;
    }
}

export function AddressAutocomplete({ label, value, onChange, placeholder, required }: Props) {
    const inputRef = useRef<HTMLInputElement>(null);
    const autocompleteRef = useRef<any>(null);
    const onChangeRef = useRef(onChange);
    const [inputValue, setInputValue] = useState(value);
    const [ready, setReady] = useState(false);

    // Keep callback ref current without triggering autocomplete re-initialization
    useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

    // Wait for Google Maps to load
    useEffect(() => {
        const checkGoogle = () => {
            if (window.google?.maps?.places) {
                setReady(true);
            } else {
                setTimeout(checkGoogle, 200);
            }
        };
        checkGoogle();
    }, []);

    // Initialise autocomplete once when Google is ready — never re-run on prop changes
    // so a pre-filled default address isn't disrupted by re-initialization
    useEffect(() => {
        if (!ready || !inputRef.current) return;

        autocompleteRef.current = new window.google.maps.places.Autocomplete(
            inputRef.current,
            {
                types: ['geocode'],
                componentRestrictions: { country: 'us' },
                fields: ['formatted_address', 'geometry', 'address_components'],
            }
        );

        // Reset placeholder after Google overwrites it
        inputRef.current.placeholder = placeholder || 'Start typing an address';

        autocompleteRef.current.addListener('place_changed', () => {
            const place = autocompleteRef.current.getPlace();
            if (!place.geometry) return;
            const address = place.formatted_address;
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            setInputValue(address);
            onChangeRef.current({ address, lat, lng, ...parseAddressComponents(place.address_components) });
        });

        return () => {
            if (window.google?.maps?.event && autocompleteRef.current) {
                window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
            }
        };
    }, [ready]); // placeholder intentionally omitted — Google sets it once at init

    // Sync external value changes (e.g. profile default address loading in)
    useEffect(() => {
        setInputValue(value);
    }, [value]);

    return (
        <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>
                {label}
            </label>
            <div style={{ position: 'relative' }}>
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    placeholder={placeholder || 'Start typing an address'}
                    required={required}
                    style={{
                        width: '100%',
                        padding: '10px 36px 10px 12px',
                        border: `1px solid ${C.border}`,
                        borderRadius: 8,
                        fontSize: 14,
                        color: C.text,
                        outline: 'none',
                        boxSizing: 'border-box',
                        fontFamily: 'Inter, sans-serif',
                    }}
                />
                <span style={{
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: 18,
                    pointerEvents: 'none',
                    color: ready ? 'var(--color-primary, #1D9E75)' : '#ccc',
                    fontWeight: 500,
                }}>
                    &#x2315;
                </span>
            </div>
            {!ready && (
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
                    Loading address search...
                </div>
            )}
        </div>
    );
}
